/**
 * Tracking pesanan — CS menjawab customer dengan angka, bukan ingatan.
 *
 * Owner 31 Agustus 2026 (K137): *"agar jika ada pertanyaan dari customer
 * menanyakan progress pengerjaan bisa dijawab secara akurat oleh customer
 * service dan muncul estimasi waktu selesainya"*.
 *
 * ---------------------------------------------------------------------------
 * ⛔ LAYAR INI DIBUKA SAMBIL MENELEPON — DAN ITU MENGUBAH SEGALANYA
 * ---------------------------------------------------------------------------
 *
 * CS sedang bicara saat membacanya. Konsekuensinya mengikat:
 *
 *  1. **Jawabannya di ATAS, jejaknya di bawah.** Yang ditanya customer cuma
 *     dua: *"sampai mana?"* dan *"kapan selesai?"*. Keduanya wajib terbaca
 *     tanpa menggulir; daftar peristiwa adalah bahan pendukung, bukan
 *     jawabannya.
 *
 *  2. **Sebab estimasi ikut ditulis.** "2 September" yang datang dari deadline
 *     yang DIJANJIKAN dan yang datang dari PERKIRAAN adalah dua kalimat
 *     berbeda di telepon — dan CS yang nol tahu bedanya akan menjanjikan yang
 *     kedua sebagai yang pertama.
 *
 *  3. **Nol ada tombol yang mengubah apa pun.** Perubahan yang lahir dari
 *     percakapan telepon adalah perubahan yang nol punya jejak persetujuan
 *     (K137).
 */
import React, { useState } from "react";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";
import { jamPesanan, useStoreTimezones } from "@/lib/timezone";
import { FA_STATUS_LABEL } from "@/lib/statusLabels";
import { WO_STATUS_TONE } from "@/components/etams/StatusBadge";

// K200 butir 8: kelompok peristiwa adalah KATEGORI, bukan status — warna status (K73/K81) tidak dipakai; bedanya di label.
const NADA = {
  DESAIN: "border-l-neutral",
  UANG: "border-l-neutral",
  ORDER: "border-l-neutral",
  PRODUKSI: "border-l-neutral",
};
// Nama kelompok peristiwa yang tampil ke staf (bukan kode mentah).
const LABEL_KELOMPOK = { DESAIN: "Desain", UANG: "Uang", ORDER: "Pesanan", PRODUKSI: "Produksi" };

export default function TrackingPesanan() {
  const [kata, setKata] = useState("");
  const [hasil, setHasil] = useState(null);
  const [jejak, setJejak] = useState(null);
  const [galat, setGalat] = useState("");
  const [sibuk, setSibuk] = useState(false);
  useStoreTimezones();   // T93: jam menurut zona cabang pesanan + label WIB/WITA

  const cari = async (e) => {
    e?.preventDefault();
    if (kata.trim().length < 2) return;
    setSibuk(true);
    setGalat("");
    setJejak(null);
    try {
      const { data } = await api.get("/tracking/cari", { params: { q: kata } });
      setHasil(data.items || []);
    } catch (err) {
      setGalat(apiErrorMessage(err));
      setHasil(null);
    } finally {
      setSibuk(false);
    }
  };

  const buka = async (fa) => {
    setSibuk(true);
    setGalat("");
    try {
      const { data } = await api.get(`/tracking/fa/${fa.id}`);
      setJejak(data);
    } catch (err) {
      setGalat(apiErrorMessage(err));
    } finally {
      setSibuk(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Tracking Pesanan</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Cari lewat nomor FA atau nama customer.
        </p>
      </div>

      <form onSubmit={cari} className="flex flex-wrap gap-2">
        <input
          autoFocus
          className="min-w-64 flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground"
          placeholder="FASBY-2608-0005 atau nama tim customer…"
          value={kata}
          onChange={(e) => setKata(e.target.value)}
        />
        <button type="submit" disabled={sibuk || kata.trim().length < 2}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50">
          {sibuk ? "Mencari…" : "Cari"}
        </button>
      </form>

      {galat && (
        <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {galat}
        </div>
      )}

      {hasil && hasil.length === 0 && (
        <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          Tidak ada pesanan yang cocok dengan "{kata}" di wilayah Anda.
        </div>
      )}

      {hasil && hasil.length > 0 && !jejak && (
        <div className="space-y-2">
          {hasil.map((f) => (
            <button key={f.id} type="button" onClick={() => buka(f)}
                    className="w-full rounded-xl border border-border bg-card p-3 text-left hover:bg-muted">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium text-foreground">{f.fa_number}</span>
                <span className="text-sm text-muted-foreground">{FA_STATUS_LABEL[f.order_status] || f.order_status}</span>
              </div>
              {/* Nama team = cara staf MENGENALI pesanan saat customer menelepon
                  ("yang basket SMA 5"). Ia ditaruh sebaris sendiri, bukan
                  disisipkan ke deretan titik-pemisah tempat ia akan hilang. */}
              {f.nama_team && (
                <p className="text-sm font-medium text-foreground">{f.nama_team}</p>
              )}
              {/* K206 / VONIS-HAK-BACA-ISI-FA: Admin Produksi + Finance menerima bentuk RINGKAS (nomor, status, tenggat)
                  — baris customer/jumlah tidak dikirim backend, jadi tidak digambar sebagai "—". */}
              {"customer_name" in f ? (
                <p className="text-sm text-muted-foreground">
                  {f.customer_name || "—"}
                  {f.customer_city ? ` (${f.customer_city})` : ""} · {f.total_qty_atasan} atasan
                  {f.deadline_date ? ` · deadline ${f.deadline_date}` : ""}
                </p>
              ) : (
                f.deadline_date && <p className="text-sm text-muted-foreground">deadline {f.deadline_date}</p>
              )}
            </button>
          ))}
        </div>
      )}

      {jejak && (
        <>
          <button type="button" onClick={() => setJejak(null)}
                  className="text-sm text-muted-foreground hover:text-foreground">
            ← kembali ke hasil pencarian
          </button>

          {/* ⛔ Butir 1 — dua jawaban yang benar-benar ditanya, di ATAS,
              terbaca tanpa menggulir. */}
          <div className="rounded-xl border border-border bg-card p-4">
            <p className="text-lg font-semibold text-foreground">
              {jejak.fa.fa_number}
              {jejak.fa.nama_team ? (
                <span className="ml-2 text-base font-medium text-foreground">
                  {jejak.fa.nama_team}
                </span>
              ) : null}
              {!jejak.ringkas && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  {jejak.fa.customer?.name || "—"}
                  {jejak.fa.customer?.city ? ` (${jejak.fa.customer.city})` : ""}
                </span>
              )}
            </p>
            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <span className="block text-xs text-muted-foreground">Sampai mana</span>
                <span className="text-base font-medium text-foreground">
                  {jejak.posisi_sekarang}
                </span>
              </div>
              <div>
                <span className="block text-xs text-muted-foreground">Perkiraan selesai</span>
                <span className="text-base font-medium text-foreground">
                  {jejak.estimasi_selesai || "belum bisa dihitung"}
                </span>
                {/* ⛔ Butir 2 — dua kalimat berbeda di telepon. */}
                {jejak.sebab_estimasi && (
                  <span className="block text-xs text-muted-foreground">
                    {jejak.sebab_estimasi === "deadline FA"
                      ? "deadline FA — dihitung dari paket, sejak DP terkonfirmasi sebelum pukul 16.00 setempat"
                      : `perkiraan — ${jejak.sebab_estimasi}`}
                  </span>
                )}
              </div>
            </div>
            {jejak.ringkas && (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="tracking-ringkas">
                Yang tampil untuk Anda: nomor FA, status, perkiraan selesai, dan posisi produksi. Isi pesanan hanya bisa
                dibuka Owner, Kepala Cabang, dan CS.
              </p>
            )}
            {jejak.wo?.length > 0 && (
              <p className="mt-3 text-sm text-muted-foreground">
                WO: {jejak.wo.map((w) => `${w.wo_number} (${WO_STATUS_TONE[w.status]?.l || w.status})`).join(", ")}
              </p>
            )}

            {/* ⛔ KALIMAT YANG BISA DIUCAPKAN — dipilih Owner sendiri,
                31 Agustus 2026, dari dua bentuk yang diajukan.

                CS membaca layar ini SAMBIL BICARA DI TELEPON. Ia nol akan
                menerjemahkan label "deadline FA" jadi kalimat yang tepat — ia
                akan MEMPARAFRASE, dan parafrase di bawah tekanan waktu adalah
                tempat janji palsu lahir. Kalimatnya disusun backend, jadi dua
                CS yang membuka FA yang sama mengucapkan hal yang sama. */}
            {jejak.ucap && (
              <div className="mt-4 rounded-lg border border-info-soft-border bg-info-soft p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-info-soft-foreground">
                  Yang bisa Anda ucapkan
                </p>
                <p className="mt-1 select-all text-base text-foreground">
                  “{jejak.ucap.kalimat}”
                </p>
                {/* ⚠ FA tanpa deadline bukan "estimasi yang lebih lemah" — ia
                    berarti NOL ADA seorang pun yang pernah menjanjikan apa pun
                    ke customer itu. Itu temuan, bukan keadaan normal. */}
                {jejak.ucap.temuan && (
                  <p className="mt-2 text-xs text-warning-soft-foreground">
                    ⚠ {jejak.ucap.temuan}
                  </p>
                )}
              </div>
            )}

            {/* ⛔ TERSENDAT DI SINI KARENA X — disetujui Owner 31 Agustus 2026
                untuk pertanyaan yang paling sering memaksa buka tiga layar:
                "kenapa FA ini belum jalan?"

                Sengaja menyebut SIAPA. "Tersendat di desain" nol menghasilkan
                tindakan; "menunggu desainer mengambil task — Admin Produksi"
                menghasilkan satu telepon ke satu orang. */}
            {jejak.tersendat ? (
              <div className="mt-3 rounded-lg border border-warning-soft-border bg-warning-soft p-3">
                <p className="text-sm font-medium text-warning-soft-foreground">
                  Tersendat di sini: {jejak.tersendat.judul}
                  {jejak.tersendat.di_mana ? ` (${jejak.tersendat.di_mana})` : ""}
                </p>
                <p className="mt-1 text-xs text-warning-soft-foreground">
                  Yang memegang: {jejak.tersendat.siapa}
                  {jejak.tersendat.diam_hari !== null &&
                   jejak.tersendat.diam_hari !== undefined
                    ? ` · sudah ${jejak.tersendat.diam_hari} hari di titik ini`
                    : ""}
                </p>
              </div>
            ) : (
              /* ⚠ Yang berjalan lancar TETAP dinyatakan. Kotak yang cuma muncul
                 saat ada masalah membuat ketiadaannya ambigu: nol ada masalah,
                 atau layarnya yang belum memuat? */
              <p className="mt-3 text-sm text-success">
                Tidak ada yang menahan pesanan ini.
              </p>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <p className="mb-3 text-sm font-medium text-foreground">
              Perjalanan pesanan — {jejak.titik.length} peristiwa
            </p>
            <div className="space-y-1">
              {jejak.titik.map((t, i) => (
                <div key={i} className={`border-l-2 ${NADA[t.kelompok] || "border-l-border"} pl-3 py-1.5`}>
                  <span className="block text-xs text-muted-foreground">
                    {jamPesanan(t.waktu, jejak.fa?.store_id, jejak.fa?.region_id)} · {LABEL_KELOMPOK[t.kelompok] || t.kelompok}
                  </span>
                  <span className="text-sm text-foreground">{t.judul}</span>
                  {t.rinci && (
                    <span className="ml-2 text-xs text-muted-foreground">{t.rinci}</span>
                  )}
                </div>
              ))}
            </div>
            {jejak.titik.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Belum ada peristiwa tercatat untuk pesanan ini.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
